import React, { useEffect, useRef, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Paperclip, FileText, Presentation, Download, Trash2, UploadCloud, FileImage,
} from "lucide-react";

const KIND_LABEL = { mom: "MOM", presentation: "Presentation", other: "Other" };
const KIND_ICON = {
  mom: FileText,
  presentation: Presentation,
  other: FileImage,
};

function fmtSize(n) {
  if (!n) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Per-review file manager — admins / PMs / BMs can upload MOM (PDF/DOC/DOCX)
 * or presentation (PPT/PPTX/ODP) files; everyone in scope can download. Stored
 * in Emergent object storage; metadata returned in `review.attachments[]`.
 */
export default function ReviewAttachmentsDialog({ review, onClose, canEdit }) {
  const [list, setList] = useState(review.attachments || []);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState("mom");
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/reviews/${review.id}`);
      setList(data.attachments || []);
    } catch {}
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [review.id]);

  const onUpload = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File exceeds 25 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      await api.post(`/reviews/${review.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Attachment uploaded");
      if (fileRef.current) fileRef.current.value = "";
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const onDownload = async (att) => {
    try {
      const token = localStorage.getItem("mptg_token");
      const res = await fetch(`${API}/reviews/${review.id}/attachments/${att.id}/download`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename || "attachment";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message || "Download failed"); }
  };

  const onDelete = async (att) => {
    if (!window.confirm(`Delete "${att.filename}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/reviews/${review.id}/attachments/${att.id}`);
      toast.success("Attachment removed");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="review-attachments-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Paperclip className="w-5 h-5 text-[#2E86C1]" />
            Attachments — {review.review_type} · {review.period_month}
          </DialogTitle>
        </DialogHeader>

        {canEdit && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="upload-section">
            <div className="text-xs font-semibold text-slate-600">Upload a new file</div>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label className="text-xs">File type</Label>
                <Select value={kind} onValueChange={setKind}>
                  <SelectTrigger className="w-44" data-testid="attachment-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mom">MOM (PDF / DOC / DOCX)</SelectItem>
                    <SelectItem value="presentation">Presentation (PPT / PPTX)</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.doc,.docx,.ppt,.pptx,.odt,.odp,.png,.jpg,.jpeg"
                onChange={onUpload}
                className="hidden"
                data-testid="attachment-file-input"
              />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="bg-[#2E86C1] hover:bg-[#2471A3] text-white"
                data-testid="attachment-upload-btn"
              >
                <UploadCloud className="w-4 h-4 mr-1" /> {busy ? "Uploading…" : "Choose file"}
              </Button>
              <span className="text-[11px] text-slate-400">Max 25 MB · PDF / DOC / DOCX / PPT / PPTX / ODT / ODP</span>
            </div>
          </div>
        )}

        <div className="mt-2 max-h-[360px] overflow-auto">
          <table className="w-full mptg-table text-sm" data-testid="attachments-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Type</th>
                <th>Size</th>
                <th>Uploaded</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr><td colSpan={5} className="py-6 text-center text-slate-400">No files yet.</td></tr>
              ) : list.map((att) => {
                const Icon = KIND_ICON[att.kind] || FileText;
                return (
                  <tr key={att.id} data-testid={`attachment-row-${att.id}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-[#2E86C1]" />
                        <div>
                          <div className="font-medium text-slate-800 break-all">{att.filename}</div>
                          <div className="text-[11px] text-slate-400">{att.content_type}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`text-xs font-semibold rounded px-2 py-0.5 ${
                        att.kind === "mom" ? "badge-blue" :
                        att.kind === "presentation" ? "badge-amber" : "badge-slate"
                      }`}>
                        {KIND_LABEL[att.kind] || att.kind}
                      </span>
                    </td>
                    <td className="tabular-nums text-xs text-slate-500">{fmtSize(att.size)}</td>
                    <td className="text-xs text-slate-500">
                      {(att.uploaded_at || "").slice(0, 10)}
                      {att.uploaded_by_name && <div className="text-[10px] text-slate-400">by {att.uploaded_by_name}</div>}
                    </td>
                    <td className="text-right whitespace-nowrap space-x-3">
                      <button onClick={() => onDownload(att)} className="text-xs text-[#1E8449] hover:underline inline-flex items-center gap-1" data-testid={`download-attachment-${att.id}`}>
                        <Download className="w-3.5 h-3.5" /> Download
                      </button>
                      {canEdit && (
                        <button onClick={() => onDelete(att)} className="text-xs text-[#C0392B] hover:underline inline-flex items-center gap-1" data-testid={`delete-attachment-${att.id}`}>
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-200">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
